import React, { useEffect, useState } from 'react';
import {
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    ListItemText,
} from '@mui/material';
import Authorization from '../../utils/Authorization';

interface Team {
    Id: string;
    Name: string;
    IsDeleted?: boolean;
}

interface TeamDropdownProps {
    teams: Team[];
    selectedTeam: string;
    onChange: (teamId: string) => void;
    showInactive?: boolean;
    onToggleInactive?: (checked: boolean) => void;
    includeAllOption?: boolean;
    label?: string;
}

const TeamDropdown: React.FC<TeamDropdownProps> = ({
    teams,
    selectedTeam,
    onChange,
    showInactive = false,
    onToggleInactive,
    includeAllOption = true,
    label = 'Team',
}) => {
    const authData = new Authorization();
    const [filteredTeams, setFilteredTeams] = useState<Team[]>([]);

    useEffect(() => {
        const filtered = showInactive ? teams : teams.filter(t => !t.IsDeleted);
        setFilteredTeams(filtered);
    }, [teams, showInactive]);

    const handleToggleInactive = (event: React.MouseEvent) => {
        event.stopPropagation(); // prevent dropdown from closing automatically
        const newValue = !showInactive;
        onToggleInactive(newValue);
        // Always reset to "All" when toggling inactive
        onChange('All');
    };

    const handleChange = (e: any) => {
        const value = e.target.value;
        if (value === '__toggle_inactive__') return; // ignore checkbox selection
        onChange(value);
    };

    return (
        <FormControl size="small" fullWidth>
            <InputLabel>{label}</InputLabel>
            <Select
                value={Boolean(selectedTeam) ? selectedTeam : authData.getDefaultTeamId('All')}
                label={label}
                onChange={handleChange}
                MenuProps={{ PaperProps: { style: { maxHeight: 300 } } }}
            >
                <MenuItem value="All">All</MenuItem>
                <MenuItem
                    value="__toggle_inactive__"
                    onClick={handleToggleInactive}
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        pl: '16px !important',
                        pr: 1,
                    }}
                >
                    <Checkbox
                        checked={showInactive}
                        size="small"
                        sx={{
                            p: 0,
                            mr: 1,
                        }}
                    />
                    <ListItemText
                        primary="Show Inactive"
                        primaryTypographyProps={{ sx: { fontSize: 15 } }}
                        sx={{ m: 0 }}
                    />
                </MenuItem>

                {filteredTeams.map((team) => (
                    <MenuItem key={team.Id} value={team.Id}
                        sx={{
                            color: team.IsDeleted ? "gray" : "black"
                        }}>
                        {team.Name}
                    </MenuItem>
                ))}
            </Select>
        </FormControl>
    );
};

export default TeamDropdown;